/**
 * ============================================================================
 * ORDERS HELPER UTILITIES
 * Context-specific helpers for building orders-related payloads
 * ============================================================================
 */

import { generateFieldsByTableColumns } from '@/utils/functions'

/**
 * Build columns object from table state
 * @param {Object} table - Table state object with columns
 * @returns {Object} Columns configuration
 */
export function buildColumnsFromTable (table) {
	if (!table?.columns || !table?.originalColumns) {
		return {}
	}

	return generateFieldsByTableColumns(
		table.columns,
		table.originalColumns
	)
}

/**
 * Build orders payload with search filters
 * @param {Object} payload - Base payload
 * @param {Object} searchFilters - Search filters from state
 * @returns {Object} Complete orders payload
 */
export function buildOrdersPayload (payload, searchFilters = {}) {
	const basePayload = { ...payload }

	// Apply search filters if present
	if (Object.keys(searchFilters).length > 0) {
		const { _page, _size, ids, ...filters } = searchFilters

		// If filtering by IDs, clear status filters
		if (ids) {
			basePayload.statuses = null
		}

		Object.assign(basePayload, filters)
	}

	return basePayload
}

/**
 * Build products payload with query type
 * @param {Object} payload - Base payload
 * @param {string} productQueryType - Type of product query (order, reservation, etc.)
 * @param {Object} searchFilters - Optional search filters
 * @returns {Object} Complete products payload
 */
export function buildProductsPayload (payload, productQueryType, searchFilters = {}) {
	const basePayload = {
		...payload,
		productQueryType
	}

	// Apply search filters if present
	if (Object.keys(searchFilters).length > 0) {
		const { _page, _size, ids, ...filters } = searchFilters

		// If filtering by IDs, clear status filters
		if (ids) {
			basePayload.statuses = null
		}

		Object.assign(basePayload, filters)
	}

	return basePayload
}

/**
 * Build returning orders payload (removes 'returning' field from filters)
 * @param {Object} payload - Base payload
 * @param {Object} searchFilters - Search filters from state
 * @returns {Object} Complete returning orders payload
 */
export function buildReturningOrdersPayload (payload, searchFilters = {}) {
	const basePayload = buildOrdersPayload(payload, searchFilters)

	// Remove 'returning' field as it's not needed for returning order queries
	delete basePayload.returning

	return basePayload
}

/**
 * Build supplier orders payload with specific search filters
 * @param {Object} payload - Base payload
 * @param {Object} searchFilters - Supplier order search filters from state
 * @returns {Object} Complete supplier orders payload
 */
export function buildSupplierOrdersPayload (payload, searchFilters = {}) {
	return buildOrdersPayload(payload, searchFilters)
}

/**
 * Build search suggestions payload with department context
 * @param {Object} payload - Base payload with optional departmentId
 * @param {Object} rootState - Vuex root state
 * @returns {Object} Search suggestions payload
 */
export function buildSearchSuggestionsPayload (payload, rootState) {
	const { departmentId, ...rest } = payload
	const result = { ...rest }

	// Use provided departmentId or fall back to user's selected department
	if (departmentId) {
		result.departmentId = departmentId
	} else if (rootState?.Auth?.user?.selectedDepartmentId) {
		result.departmentId = rootState.Auth.user.selectedDepartmentId
	}

	return result
}
